import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle, FiArrowRight, FiClock, FiEye, FiGrid, FiHelpCircle, FiList, FiUsers, FiVideo } = FiIcons;

const CARD_STYLES = {
  blue: {
    glow: 'bg-blue-500/10',
    iconWrap: 'bg-blue-500/10 border-blue-500/20',
    icon: 'text-blue-400'
  },
  green: {
    glow: 'bg-green-500/10',
    iconWrap: 'bg-green-500/10 border-green-500/20',
    icon: 'text-green-400'
  },
  purple: {
    glow: 'bg-purple-500/10',
    iconWrap: 'bg-purple-500/10 border-purple-500/20',
    icon: 'text-purple-400'
  },
  orange: {
    glow: 'bg-orange-500/10',
    iconWrap: 'bg-orange-500/10 border-orange-500/20',
    icon: 'text-orange-400'
  }
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalApps: 0,
    totalVideos: 0,
    totalTopics: 0,
    publishedClientVideos: 0,
    publishedInternalVideos: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [appsResult, videosResult, topicsResult, clientVideosResult, internalVideosResult, recentVideosResult] = await Promise.all([
        apiCall(() => pb.collection('apps').getList(1, 1)),
        apiCall(() => pb.collection('videos').getList(1, 1)),
        apiCall(() => pb.collection('playlists').getList(1, 1, { filter: 'workflow_status != "archived"' })),
        apiCall(() => pb.collection('videos').getList(1, 1, {
          filter: 'visibility_scope = "client_facing" && workflow_status = "published"'
        })),
        apiCall(() => pb.collection('videos').getList(1, 1, {
          filter: 'visibility_scope = "bpas_internal_team" && workflow_status = "published"'
        })),
        apiCall(() => pb.collection('videos').getList(1, 10, {
          sort: '-updated',
          filter: 'workflow_status != "archived"'
        }))
      ]);

      setStats({
        totalApps: appsResult.totalItems,
        totalVideos: videosResult.totalItems,
        totalTopics: topicsResult.totalItems,
        publishedClientVideos: clientVideosResult.totalItems,
        publishedInternalVideos: internalVideosResult.totalItems
      });
      setRecentActivity(recentVideosResult.items);
    } catch (loadError) {
      console.error('Failed to load dashboard data:', loadError);
      setError('Could not load dashboard data. Confirm PocketBase access and try again.');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, tone }) => {
    const style = CARD_STYLES[tone] || CARD_STYLES.blue;

    return (
      <div className="bg-slate-900 rounded-xl p-6 border border-slate-800 shadow-sm relative overflow-hidden group hover:border-slate-700 transition-all">
        <div className={`absolute top-0 right-0 w-24 h-24 ${style.glow} rounded-full blur-2xl -mr-8 -mt-8 pointer-events-none`} />
        <div className="flex items-center justify-between relative z-10">
          <div>
            <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
            <p className="text-3xl font-bold text-white tracking-tight">
              {loading ? '...' : Number(value || 0).toLocaleString()}
            </p>
          </div>
          <div className={`p-3 rounded-xl border ${style.iconWrap}`}>
            <SafeIcon icon={icon} className={`w-6 h-6 ${style.icon}`} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Overview of your Video Hub</p>
        </div>
        {error && (
          <button type="button" onClick={loadDashboardData} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm">
            Retry
          </button>
        )}
      </div>

      <div className="mb-6 bg-slate-900 border border-slate-800 rounded-2xl p-5 lg:p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white">New to Video Hub?</p>
          <p className="text-sm text-slate-500 mt-1 leading-5">Follow the App-first guide for minimal-entry videos, Topics, Smart Search, publishing checks and the dynamic client Training Centre integration prompt.</p>
        </div>
        <Link to="/help" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold flex-shrink-0">
          Open Help &amp; FAQ
          <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
        </Link>
      </div>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/20 text-red-200 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5 mb-8">
        <StatCard title="Total Apps" value={stats.totalApps} icon={FiGrid} tone="blue" />
        <StatCard title="Total Videos" value={stats.totalVideos} icon={FiVideo} tone="green" />
        <StatCard title="Topics" value={stats.totalTopics} icon={FiList} tone="blue" />
        <StatCard title="Published Client" value={stats.publishedClientVideos} icon={FiEye} tone="purple" />
        <StatCard title="Published Internal" value={stats.publishedInternalVideos} icon={FiUsers} tone="orange" />
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-sm">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center">
            <SafeIcon icon={FiClock} className="w-5 h-5 text-slate-400 mr-2" />
            <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="animate-pulse flex items-center space-x-4">
                  <div className="w-20 h-12 bg-slate-800 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-800 rounded w-3/4" />
                    <div className="h-3 bg-slate-800 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivity.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {recentActivity.map(video => (
                <Link key={video.id} to={`/videos/${video.id}`} className="flex items-center space-x-4 p-4 bg-slate-800/30 rounded-xl border border-slate-800 hover:bg-slate-800 hover:border-slate-700 transition-all">
                  <div className="w-24 aspect-video bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border border-slate-700">
                    {video.thumbnail_url ? <img src={video.thumbnail_url} alt={`${video.title || 'Training video'} thumbnail`} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiVideo} className="w-5 h-5 text-blue-400" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{video.title}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-1">{video.description || 'No description added yet.'}</p>
                    <div className="flex items-center space-x-3 mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        video.workflow_status === 'published'
                          ? 'bg-green-500/10 text-green-400'
                          : video.workflow_status === 'review'
                            ? 'bg-yellow-500/10 text-yellow-400'
                            : 'bg-slate-700 text-slate-400'
                      }`}>
                        {video.workflow_status}
                      </span>
                      <span className="text-xs text-slate-500">
                        {video.updated ? format(new Date(video.updated), 'dd MMM, HH:mm') : '—'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;